import React, { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import FacebookRoundedIcon from '@mui/icons-material/FacebookRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import type { ActionButtonComponentConfig, FlowComponentRenderProps } from '../../types';

const ActionButtonBlock: React.FC<FlowComponentRenderProps<ActionButtonComponentConfig>> = ({
  config,
  goBack,
  goNext,
  goToScreen,
  onAction,
  onInteract,
  setValue,
  values,
}) => {
  const [referralExpanded, setReferralExpanded] = useState(true);
  const isOutlined = config.style === 'outlined';
  const isText = config.style === 'text';
  const showArrow = config.showArrow ?? true;
  const textAlign = config.textAlign ?? 'left';
  const backgroundColor =
    config.backgroundColor ??
    (isText
      ? 'transparent'
      : isOutlined
        ? 'var(--cx-brand-button-bg)'
        : 'var(--cx-brand-primary)');
  const borderColor =
    config.borderColor ??
    (isText
      ? 'transparent'
      : isOutlined
        ? 'var(--cx-brand-button-border)'
        : 'var(--cx-brand-primary)');
  const textColor =
    config.textColor ??
    (isText || isOutlined ? 'var(--cx-brand-button-text)' : 'var(--cx-brand-primary-contrast)');
  const hoverBackgroundColor =
    config.hoverBackgroundColor ??
    (isText || isOutlined
      ? 'var(--cx-brand-button-hover-bg)'
      : 'var(--cx-brand-primary-dark)');
  const isReferralCard = config.action === 'referral';

  const handleAction = () => {
    if (onInteract) {
      onInteract({ componentId: config.id, label: config.label, phone: config.phone, type: config.action, url: config.url, value: config.routeValue ?? true });
      return;
    }
    if (config.routeValue !== undefined) {
      setValue(config.routeValueKey ?? config.id, config.routeValue);
    }

    if (config.action === 'edit_response') {
      Object.entries(values).forEach(([componentId, value]) => {
        if (!value || typeof value !== 'object') return;

        const response = value as { score?: unknown; submitted?: unknown };
        if (typeof response.score !== 'number' || response.submitted !== true) return;

        setValue(componentId, { ...response, submitted: false });
      });
    }

    onAction?.({
      componentId: config.id,
      label: config.label,
      phone: config.phone,
      type: config.action,
      url: config.url,
      value: config.routeValue ?? true,
    });

    if (config.action === 'edit_response') {
      if (config.targetScreenId) {
        goToScreen(config.targetScreenId);
        return;
      }

      goBack?.();
      return;
    }

    if (config.action === 'continue') {
      if (config.targetScreenId) {
        goToScreen(config.targetScreenId);
        return;
      }

      goNext();
    }
  };

  const shareReferral = async (destination: 'copy' | 'facebook' | 'whatsapp' | 'share') => {
    const url = config.url ? new URL(config.url, window.location.origin).toString() : window.location.href;
    if (destination === 'copy') {
      if (navigator.clipboard) await navigator.clipboard.writeText(url);
      return;
    }
    if (destination === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (destination === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodeURIComponent(`I thought you might like this service. ${url}`)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (navigator.share) {
      await navigator.share({ title: 'Referral', url });
      return;
    }
    if (navigator.clipboard) await navigator.clipboard.writeText(url);
  };

  if (isReferralCard) {
    const shareActions = [
      { label: 'Copy link', destination: 'copy' as const, icon: ContentCopyRoundedIcon },
      { label: 'Share on Facebook', destination: 'facebook' as const, icon: FacebookRoundedIcon },
      { label: 'Share on WhatsApp', destination: 'whatsapp' as const, icon: WhatsAppIcon },
      { label: 'Share referral', destination: 'share' as const, icon: ShareRoundedIcon },
    ];

    if (!referralExpanded) {
      return (
        <Card
          sx={{
            background: 'var(--color-base-white)',
            border: '1px solid var(--cx-feedback-border, rgba(145, 158, 171, 0.22))',
            borderRadius: 3,
            boxShadow: '0 10px 28px rgba(23, 26, 24, 0.08)',
          }}
        >
          <Button
            aria-expanded="false"
            endIcon={<ExpandMoreRoundedIcon />}
            fullWidth
            onClick={() => setReferralExpanded(true)}
            startIcon={<CardGiftcardRoundedIcon />}
            sx={{
              color: 'var(--cx-feedback-text, #1F2937)',
              fontWeight: 900,
              justifyContent: 'space-between',
              minHeight: 64,
              px: 2.5,
              textTransform: 'none',
            }}
            type="button"
          >
            Refer a friend
          </Button>
        </Card>
      );
    }

    return (
      <Card
        sx={{
          background: 'var(--color-base-white)',
          border: '1px solid var(--cx-feedback-border, rgba(145, 158, 171, 0.22))',
          borderRadius: 3,
          boxShadow: '0 10px 28px rgba(23, 26, 24, 0.08)',
          color: 'var(--cx-feedback-text, #1F2937)',
          overflow: 'hidden',
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <Stack spacing={2.25} sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              alignSelf: 'center',
              bgcolor: 'var(--cx-brand-primary-soft)',
              borderRadius: 2,
              display: 'flex',
              height: 52,
              justifyContent: 'center',
              width: 52,
              color: 'var(--cx-brand-primary)',
            }}
          >
            <CardGiftcardRoundedIcon />
          </Box>

          <Stack spacing={0.75}>
            <Typography sx={{ color: 'inherit', fontSize: { xs: 30, sm: 34 }, fontWeight: 900, lineHeight: 1.1 }}>
              Refer a friend
            </Typography>
            <Typography sx={{ color: 'var(--cx-feedback-muted, #637381)', fontSize: 15, lineHeight: 1.55 }}>
              They save $50, and you earn $50 after their first paid visit.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1.25} sx={{ justifyContent: 'center' }}>
            {shareActions.map((action) => {
              const Icon = action.icon;
              return (
                <IconButton
                  aria-label={action.label}
                  key={action.destination}
                  onClick={() => {
                    handleAction();
                    void shareReferral(action.destination).catch(() => undefined);
                  }}
                  sx={{
                    bgcolor: 'var(--cx-brand-primary-soft)',
                    color: 'var(--cx-brand-primary)',
                    height: 44,
                    width: 44,
                    '&:hover': { bgcolor: 'color-mix(in srgb, var(--cx-brand-primary) 18%, transparent)' },
                  }}
                >
                  <Icon />
                </IconButton>
              );
            })}
          </Stack>

          <Divider sx={{ borderColor: 'var(--cx-feedback-border, rgba(145, 158, 171, 0.22))' }} />
          <Stack direction="row" spacing={1} sx={{ justifyContent: 'center' }}>
            <Button
              onClick={() => setReferralExpanded(false)}
              sx={{ color: 'var(--cx-feedback-muted, #637381)', fontWeight: 800 }}
              type="button"
              variant="text"
            >
              Skip
            </Button>
            <Button
              onClick={() => window.location.assign('/preview/referral-auth')}
              startIcon={<AccountBalanceWalletRoundedIcon />}
              sx={{ color: 'var(--cx-brand-primary)', fontWeight: 800 }}
              type="button"
              variant="text"
            >
              Rewards portal
            </Button>
          </Stack>
        </Stack>
      </Card>
    );
  }

  return (
    <Button
      fullWidth
      onClick={handleAction}
      style={
        {
          '--cx-action-button-bg': backgroundColor,
          '--cx-action-button-border': borderColor,
          '--cx-action-button-color': textColor,
          '--cx-action-button-hover-bg': hoverBackgroundColor,
          ...(config.borderRadius !== undefined
            ? { borderRadius: `${config.borderRadius}px` }
            : {}),
          ...(config.minHeight !== undefined ? { minHeight: `${config.minHeight}px` } : {}),
        } as React.CSSProperties
      }
      sx={{
        bgcolor: 'var(--cx-action-button-bg)',
        border: isText ? 0 : '1px solid',
        borderColor: 'var(--cx-action-button-border)',
        borderRadius: config.borderRadius !== undefined ? `${config.borderRadius}px` : undefined,
        color: 'var(--cx-action-button-color)',
        fontWeight: 900,
        justifyContent: showArrow ? 'space-between' : 'center',
        minHeight: config.minHeight ?? (isText ? 30 : 48),
        px: 1.5,
        py: 1.15,
        textTransform: 'none',
        '&:hover': {
          bgcolor: 'var(--cx-action-button-hover-bg)',
          borderColor: 'var(--cx-action-button-border)',
        },
      }}
      type="button"
      variant={isText ? 'text' : isOutlined ? 'outlined' : 'contained'}
    >
      <Box sx={{ minWidth: 0, textAlign, flex: showArrow ? 1 : undefined }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: 'inherit',
            fontSize: config.fontSize ?? 15,
            fontWeight: 900,
            lineHeight: 1.6,
          }}
        >
          {config.label}
        </Typography>
        {config.description ? (
          <Typography variant="caption" sx={{ color: 'inherit', display: 'block', opacity: 0.74 }}>
            {config.description}
          </Typography>
        ) : null}
      </Box>
      {showArrow ? <ArrowForwardRoundedIcon sx={{ flexShrink: 0, ml: 1 }} /> : null}
    </Button>
  );
};

export default ActionButtonBlock;
